import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { sortBySortOrder } from '@ml/common';
import {
  BaseDesignGalleryImageVM,
  InteriorDesignGalleryImageType,
  InteriorDesignGalleryImageVM,
  InteriorOrderAccordionContentTypes
} from './interior-design-gallery-image-vm';
import { SvgHelperComponent } from '../svg-helper/svg-helper.component';
import { ModalComponent } from '../modal/modal.component';
import { CapitalizeFirstLetterPipe } from '../capitalize-first-letter.pipe';

@Component({
  selector: 'design-gallery',
  templateUrl: './interior-design-gallery.component.html',
  styleUrl: './interior-design-gallery.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, NgClass, SvgHelperComponent, ModalComponent, CapitalizeFirstLetterPipe]
})
export class InteriorDesignGalleryComponent implements OnInit, OnChanges {
  @Input() images: InteriorDesignGalleryImageVM[] | BaseDesignGalleryImageVM[] = [];
  @Input() selectedImage: InteriorDesignGalleryImageVM | BaseDesignGalleryImageVM;
  @Input() allFPRoomsDesignSelections: InteriorDesignGalleryImageVM[] = [];
  @Input() interiorContentType: InteriorOrderAccordionContentTypes;
  @Input() isNextBtnDisabled = false;
  @Input() selectedCameraAngle: string = '';
  @Input() displayOnly = false;
  @Input() showSelectedDesigns = true;
  @Output() selectImage = new EventEmitter<
    InteriorDesignGalleryImageVM | BaseDesignGalleryImageVM
  >();
  @Output() openNextAccordion = new EventEmitter<InteriorOrderAccordionContentTypes>();
  @Output() selectCameraAngle = new EventEmitter<string>();
  @ViewChild('designThumbnailContainer') designThumbnailContainer: ElementRef;
  currentImage: InteriorDesignGalleryImageVM | BaseDesignGalleryImageVM;
  imageTypes = InteriorDesignGalleryImageType;
  selectedImageType: InteriorDesignGalleryImageType;
  roomTypes: string[] = [];
  selectedRoomType: string = '';
  designTypes: string[] = [];
  selectedDesignType: string = '';
  filteredImages: InteriorDesignGalleryImageVM[] | BaseDesignGalleryImageVM[] = [];
  selectedIndex: number = 0;
  selectedModalDesignImage: InteriorDesignGalleryImageVM;
  interiorContentTypes = InteriorOrderAccordionContentTypes;
  roomTypeLabel = 'Room Type';
  selectButtonLabel = 'Image';
  showNextButton = false;

  constructor() {}

  ngOnInit(): void {
    this.showNextButton =
      this.interiorContentType !== InteriorOrderAccordionContentTypes.Environment &&
      this.openNextAccordion.observed;
    this.interiorContentType === InteriorOrderAccordionContentTypes.Environment
      ? (this.selectedImageType = InteriorDesignGalleryImageType.Standard)
      : (this.selectedImageType = InteriorDesignGalleryImageType.GreyBox);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.images) {
      if (this.images.length) {
        this.roomTypes = [
          ...new Set(this.images.sort(sortBySortOrder).map(image => image.RoomTypeDisplayName))
        ];
        if (
          this.selectedImage &&
          this.images.findIndex(image => image.Id === this.selectedImage.Id) > -1
        ) {
          const foundImage = this.images.find(image => image.Id === this.selectedImage.Id);
          this.currentImage = foundImage;
          this.selectedRoomType = foundImage.RoomTypeDisplayName;
          this.designTypes = [
            ...new Set(
              this.images
                .filter(x => x.RoomTypeDisplayName === this.selectedRoomType)
                .map(x => x.DesignType)
            )
          ];

          this.selectedDesignType = foundImage.DesignType;
          this.selectedIndex = this.filteredImages.findIndex(
            image => image.Id === this.selectedImage.Id
          );
        } else if (this.selectedRoomType && this.roomTypes.indexOf(this.selectedRoomType) > -1) {
          this.selectedDesignType = '';
          this.selectedIndex = 0;
          this.currentImage = this.filteredImages[0];
        } else {
          this.selectedRoomType = '';
          this.selectedDesignType = '';
          this.selectedIndex = 0;
          this.currentImage = this.filteredImages[0];
        }
      }
      if (this.selectedCameraAngle) {
        this.selectedRoomType = this.selectedCameraAngle;
      }

      if (
        this.displayOnly &&
        this.interiorContentType !== InteriorOrderAccordionContentTypes.Environment &&
        !this.selectedRoomType
      ) {
        this.handleRoomTypeChange(this.roomTypes[0]);
      }

      this.filterImages();
    }
  }

  handleImageChange(image: InteriorDesignGalleryImageVM | BaseDesignGalleryImageVM) {
    if (image.Id === this.currentImage?.Id) return;

    this.selectedIndex = this.filteredImages.findIndex(d => d.Id === image.Id);
    this.currentImage = image;
  }

  handleImageSelect() {
    this.currentImage.SelectedImageType = this.selectedImageType;
    this.selectImage.emit(this.currentImage);
  }

  handleOpenNextAccordion() {
    this.openNextAccordion.emit(this.interiorContentType);
  }

  handleArrowKeys(event: KeyboardEvent) {
    event.preventDefault();
    if (event.key === 'ArrowUp') {
      const currentIndex = this.filteredImages.findIndex(
        image => image.Id === this.currentImage.Id
      );

      if (currentIndex > 0) {
        this.currentImage = this.filteredImages[currentIndex - 1];
        this.selectedIndex = currentIndex - 1;
        this.scrollIntoView();
      }
    } else if (event.key === 'ArrowDown') {
      const currentIndex = this.filteredImages.findIndex(
        image => image.Id === this.currentImage.Id
      );
      if (currentIndex < this.filteredImages.length - 1) {
        this.currentImage = this.filteredImages[currentIndex + 1];
        this.selectedIndex = currentIndex + 1;
        this.scrollIntoView();
      }
    }
  }

  handleToggleImageType(type: InteriorDesignGalleryImageType) {
    if (this.selectedImageType === type) return;
    this.selectedImageType = type;
  }

  handleRoomTypeChange(roomType: string) {
    const previousValue = this.selectedRoomType;
    this.selectedRoomType = roomType;
    if (this.selectedDesignType) {
      this.selectedDesignType = '';
    }
    if (previousValue !== roomType) {
      this.designTypes = [
        ...new Set(
          this.images.filter(x => x.RoomTypeDisplayName === roomType).map(x => x.DesignType)
        )
      ];
      if (this.designTypes.length === 1) {
        this.selectedDesignType = this.designTypes[0];
      }

      if (this.interiorContentType === InteriorOrderAccordionContentTypes.CameraAngle) {
        this.selectCameraAngle.emit(this.selectedRoomType);
      }

      this.filterImages();
    }
  }

  handleDesignTypeChange(designType: string) {
    const previousValue = this.selectedDesignType;
    this.selectedDesignType = designType;
    if (previousValue !== designType) {
      this.filterImages();
    }
  }

  handleModalImageSelect(image: InteriorDesignGalleryImageVM) {
    this.selectedModalDesignImage = image;
  }
  handleModalClose() {
    this.selectedModalDesignImage = null;
  }

  filterImages() {
    if (this.interiorContentType !== this.interiorContentTypes.Environment) {
      if (this.selectedRoomType && this.selectedDesignType) {
        this.filteredImages = this.images.filter(
          image =>
            (image.RoomType === this.selectedRoomType ||
              image.RoomTypeDisplayName === this.selectedRoomType) &&
            image.DesignType === this.selectedDesignType
        );
      } else if (this.selectedRoomType) {
        this.filteredImages = this.images.filter(image => {
          if (this.selectedDesignType) {
            return (
              (image.RoomType === this.selectedRoomType ||
                image.RoomTypeDisplayName === this.selectedRoomType) &&
              image.DesignType === this.selectedDesignType
            );
          }
          return (
            image.RoomType === this.selectedRoomType ||
            image.RoomTypeDisplayName === this.selectedRoomType
          );
        });
      } else if (this.selectedDesignType) {
        this.filteredImages = this.images.filter(
          image => image.DesignType === this.selectedDesignType
        );
      } else {
        this.filteredImages = this.images;
      }
    } else {
      this.filteredImages = this.images;
    }

    if (this.filteredImages.length > 0) {
      if (
        this.selectedImage &&
        this.filteredImages.findIndex(image => image.Id === this.selectedImage.Id) > -1
      ) {
        this.currentImage = this.filteredImages.find(image => image.Id === this.selectedImage.Id);
      } else {
        this.currentImage = this.filteredImages[0];
      }
    }
  }

  trackById(_, image: InteriorDesignGalleryImageVM | BaseDesignGalleryImageVM) {
    return image.Id;
  }

  private scrollIntoView() {
    setTimeout(() => {
      const thumbnails =
        this.designThumbnailContainer.nativeElement.querySelectorAll('.design-thumbnail');
      if (thumbnails && thumbnails[this.selectedIndex]) {
        thumbnails[this.selectedIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    });
  }
}
